import * as React from 'react';
import { css, Global } from '@emotion/react';
import {
  theme,
  BreakpointProvider,
  Draft,
  ThemeProvider,
  ToastProvider,
  BreakpointConsumer,
} from '@resi-media/resi-ui';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { BrowserRouter as Router } from 'react-router';
import { SWRConfig } from 'swr';
import { reset } from '@studio/styles/reset-css';
import {
  INTEGRATION,
  PRODUCTION,
  ONE_HUNDRED_PERCENT,
  TWENTY_PERCENT,
  SENTRY_ENV_INT,
  SENTRY_ENV_PROD,
} from './constants/env-variables';
import { EnvironmentProvider } from './contexts/environmentContext';
import CustomError from './helpers/customError';
import Routes from './routes';

const App = () => {
  const environmentVariables = React.useMemo(() => {
    const isBuildEnvIntegration = window.location.href.includes('buildEnv=integration');
    const isBuildEnvProduction = window.location.href.includes('buildEnv=production');

    if (!isBuildEnvProduction && !isBuildEnvIntegration) {
      switch (true) {
        case /[-_a-zA-Z0-9]*.resi-sites.pages.dev/.test(window.location.host):
        case /int-sites.resi.io/.test(window.location.host):
        case new RegExp('localhost:port'.replace('port', `${process.env.VITE_LOCAL_HOST_PORT}`)).test(
          window.location.host
        ):
        case new RegExp('127.0.0.1:port'.replace('port', `${process.env.VITE_LOCAL_HOST_PORT}`)).test(
          window.location.host
        ):
          if (process.env.NODE_ENV !== 'development') {
            Sentry.init({
              dsn: 'https://7402ab6be4929868064d81cf86c278e4@o1151571.ingest.sentry.io/4505783782277120',
              environment: SENTRY_ENV_INT,
              integrations: [browserTracingIntegration()],
              release: import.meta.env.VITE_APP_VERSION,
              tracesSampleRate: ONE_HUNDRED_PERCENT,
              ignoreErrors: [
                'ResizeObserver loop limit exceeded',
                'ResizeObserver loop completed with undelivered notifications.',
                'timeout of 0ms exceeded',
              ],
            });
          }
          return INTEGRATION;
        default:
          if (process.env.NODE_ENV !== 'development') {
            Sentry.init({
              dsn: 'https://7402ab6be4929868064d81cf86c278e4@o1151571.ingest.sentry.io/4505783782277120',
              environment: SENTRY_ENV_PROD,
              integrations: [browserTracingIntegration()],
              release: import.meta.env.VITE_APP_VERSION,
              tracesSampleRate: TWENTY_PERCENT,
              ignoreErrors: [
                'ResizeObserver loop limit exceeded',
                'ResizeObserver loop completed with undelivered notifications.',
                'timeout of 0ms exceeded',
              ],
            });
          }
          return PRODUCTION;
      }
    } else if (isBuildEnvIntegration) {
      return INTEGRATION;
    } else {
      return PRODUCTION;
    }
  }, []);

  const LDProvider = withLDProvider({
    clientSideID: environmentVariables.ldClientSideId,
    context: { anonymous: true },
  })(() => (
    <EnvironmentProvider {...environmentVariables}>
      <BreakpointProvider queries={theme.mq}>
        <BreakpointConsumer>
          {() => {
            const isDesktopPage = !document.querySelector("meta[content='width=device-width, initial-scale=1.0']");
            return (
              <ThemeProvider
                theme={{
                  ...theme,
                  mq: {
                    xs: isDesktopPage ? '@media all' : theme.mq.xs,
                    sm: isDesktopPage ? '@media all' : theme.mq.sm,
                    md: isDesktopPage ? '@media all' : theme.mq.md,
                    lg: isDesktopPage ? '@media all' : theme.mq.lg,
                    xl: isDesktopPage ? '@media all' : theme.mq.xl,
                    xxl: isDesktopPage ? '@media all' : theme.mq.xxl,
                  },
                }}
              >
                <Global
                  styles={(currentTheme) => css`
                    ${reset}
                    body {
                      text-rendering: optimizeLegibility;
                      background: ${currentTheme.palette.contrastInverse};
                      font-family: ${currentTheme.typography.fontFamilyBody};
                      margin: 0;
                      overflow: auto;
                      height: 100%;
                    }
                  `}
                />
                <ToastProvider>
                  <Draft.LocalizationProvider>
                    <SWRConfig
                      value={{
                        provider: () => new Map(),
                        revalidateIfStale: false,
                        keepPreviousData: true,
                        fetcher: ([resource, token], _init) => {
                          return fetch(resource, {
                            headers: new Headers({
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                            }),
                          }).then(async (res) => {
                            if (!res.ok) {
                              const error = await res.json();

                              if (error?.errors) {
                                throw new CustomError('Fetching Error', { status: res.status, info: error.errors });
                              } else {
                                throw new CustomError(error.message, { status: res.status });
                              }
                            }
                            return res.json();
                          });
                        },
                        errorRetryCount: 3,
                        onErrorRetry: (error, key, config, revalidate, opts) => {
                          const maxRetryCount = config.errorRetryCount;
                          const retryCount = opts.retryCount;
                          const errorsToRetry = [429, 502, 503];
                          // Never retry on 404.
                          if (!errorsToRetry.includes(error.status)) return;

                          // Exponential backoff
                          const timeout =
                            ~~((Math.random() + 0.5) * (1 << (retryCount < 8 ? retryCount : 8))) *
                            config.errorRetryInterval;

                          if (maxRetryCount !== undefined && retryCount > maxRetryCount) {
                            return;
                          }

                          console.error(`Retry attempt #${retryCount}, ${key} ${config}`);
                          // Retry after 10 milliseconds.
                          // Copied exponential backoffType from retry-axios like Studio
                          setTimeout(revalidate, timeout, opts);
                        },
                      }}
                    >
                      <Router>
                        <Draft.ModalProvider appElement="#app">
                          <Routes />
                        </Draft.ModalProvider>
                      </Router>
                    </SWRConfig>
                  </Draft.LocalizationProvider>
                </ToastProvider>
              </ThemeProvider>
            );
          }}
        </BreakpointConsumer>
      </BreakpointProvider>
    </EnvironmentProvider>
  ));

  return <LDProvider />;
};

export default App;
